import { useAtom, useSetAtom } from "jotai";
import { RESET } from "jotai/utils";
import { useCallback, useMemo } from "react";
import ViewSlider, { ViewProps } from "react-view-slider";
import {
  uploadSidePanelAtom,
  uploadSidePanelViewAtom,
} from "../../../../../../../../atoms/location/uploadSidePanelAtom";
import { uploadReleaseTrackStateAtom } from "../../../../../../../../atoms/releases/uploadReleaseTrackAtom";
import { useMediaQueryBreakpoint } from "../../../../../../../../hooks/useMediaQuery";
import { PlaylistTrack } from "../../../../../../../../store/models/playListTrack/types";
import { WORKFLOW_SIDE_PANEL_PADDING } from "../../../../../../../components/ProjectWorkflowPanel/ProjectWorkflowPanel.styles";
import { WrappedEditReleaseTrackForm } from "../../../../../../../components/ReleaseTrack/EditReleaseTrackForm/WrappedEditReleaseTrackForm";
import { UploadReleaseTrackForm } from "../../../../../../../components/ReleaseTrack/UploadReleaseTrackForm/UploadReleaseTrackForm";
import { SidePanel } from "../../../../../../../components/SidePanel/SidePanel";

export enum UploadReleaseTrackSidePanelView {
  UPLOAD_RELEASE_TRACK,
  EDIT_RELEASE_TRACK,
}

interface AddSongsSidePanelProps {
  releaseId: number;
  releaseTrackId?: number;
  uploadedTrack?: PlaylistTrack;
}

export const AddSongsSidePanel = ({
  releaseId,
  releaseTrackId,
  uploadedTrack,
}: AddSongsSidePanelProps) => {
  const { isDesktop } = useMediaQueryBreakpoint();
  const [activeView, setActiveView] = useAtom(uploadSidePanelViewAtom);
  const [isSidePanelOpen, setIsSidePanelOpen] = useAtom(uploadSidePanelAtom);
  const setUploadReleaseTrackState = useSetAtom(uploadReleaseTrackStateAtom);

  const handleClose = useCallback(() => {
    setActiveView(UploadReleaseTrackSidePanelView.UPLOAD_RELEASE_TRACK);
    setIsSidePanelOpen(false);
    setUploadReleaseTrackState(RESET);
  }, [setIsSidePanelOpen, setUploadReleaseTrackState, setActiveView]);

  const UPLOAD_RELEASE_TRACK_VIEWS: { [key: number]: JSX.Element } = useMemo(
    () => ({
      [UploadReleaseTrackSidePanelView.UPLOAD_RELEASE_TRACK]: (
        <UploadReleaseTrackForm
          handleCancel={() => {
            handleClose();
          }}
          handleSave={() =>
            setActiveView(UploadReleaseTrackSidePanelView.EDIT_RELEASE_TRACK)
          }
          releaseId={releaseId}
          releaseTrackId={releaseTrackId}
          uploadedTrack={uploadedTrack}
        />
      ),
      [UploadReleaseTrackSidePanelView.EDIT_RELEASE_TRACK]: (
        <WrappedEditReleaseTrackForm
          handleCancel={() => {
            setActiveView(UploadReleaseTrackSidePanelView.UPLOAD_RELEASE_TRACK);
            handleClose();
          }}
          releaseTrackId={releaseTrackId}
          handleSave={() => {
            setUploadReleaseTrackState(RESET);
            setActiveView(UploadReleaseTrackSidePanelView.UPLOAD_RELEASE_TRACK);
          }}
        />
      ),
    }),
    [
      releaseId,
      handleClose,
      uploadedTrack,
      releaseTrackId,
      setUploadReleaseTrackState,
      setActiveView,
    ],
  );

  const renderView = ({ index }: ViewProps) => {
    return UPLOAD_RELEASE_TRACK_VIEWS[index];
  };
  return (
    <SidePanel
      fillScreen={!isDesktop}
      isOpen={isSidePanelOpen}
      onClose={handleClose}
      sidePanelStyle={{
        padding: WORKFLOW_SIDE_PANEL_PADDING,
        ...(isDesktop && { maxWidth: "800px" }),
      }}
    >
      <ViewSlider
        keepViewsMounted={true}
        renderView={renderView}
        numViews={Object.keys(UPLOAD_RELEASE_TRACK_VIEWS).length}
        activeView={activeView}
        animateHeight
      />
    </SidePanel>
  );
};
